import { useState, useRef, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  alpha,
  darken,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendIcon from "@mui/icons-material/Send";
import SmartToyTwoToneIcon from "@mui/icons-material/SmartToyTwoTone";
import SchoolTwoToneIcon from "@mui/icons-material/SchoolTwoTone";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import { TutorialSandbox } from "./tutorial/TutorialSandbox.tsx";

type Props = {
  open: boolean;
  onClose: () => void;
  paletteColor: PaletteColor;
  isDark?: boolean;
  variant?: "overlay" | "panel";
  honbuId?: number;
  roomId?: number;
  isMobile?: boolean;
  onTaskMutated?: () => void;
};

type ChatMessage = {
  role: "user" | "model";
  text: string;
};

const DRAWER_WIDTH = 420;

function getDefaultExpanded(): string[] {
  return ["guide-quick", "g1", "faq-edit"];
}

// ===== 内部サブコンポーネント =====

const H3 = ({
  children,
  color,
}: {
  children: React.ReactNode;
  color: string;
}) => (
  <Typography
    variant="subtitle2"
    fontWeight="bold"
    sx={{ mt: 1.5, mb: 0.5, color }}
  >
    {children}
  </Typography>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }}>
    {children}
  </Typography>
);

const Hint = ({
  children,
  color,
}: {
  children: React.ReactNode;
  color: string;
}) => (
  <Box
    sx={{
      bgcolor: alpha(color, 0.08),
      borderLeft: `3px solid ${color}`,
      px: 1.5,
      py: 0.75,
      borderRadius: "0 4px 4px 0",
      mb: 1,
    }}
  >
    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
      {children}
    </Typography>
  </Box>
);

const Warn = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        bgcolor: alpha(theme.palette.warning.main, 0.1),
        borderLeft: `3px solid ${theme.palette.warning.main}`,
        px: 1.5,
        py: 0.75,
        borderRadius: "0 4px 4px 0",
        mb: 1,
      }}
    >
      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
        {children}
      </Typography>
    </Box>
  );
};

const CompactTable = ({
  headers,
  rows,
  color,
}: {
  headers: string[];
  rows: string[][];
  color: string;
}) => (
  <Box sx={{ overflowX: "auto", mb: 1 }}>
    <Table size="small" sx={{ tableLayout: "auto" }}>
      <TableHead>
        <TableRow sx={{ bgcolor: alpha(color, 0.1) }}>
          {headers.map((h) => (
            <TableCell
              key={h}
              sx={{ fontWeight: "bold", fontSize: "0.75rem", py: 0.5, px: 1 }}
            >
              {h}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => (
              <TableCell key={j} sx={{ fontSize: "0.75rem", py: 0.5, px: 1 }}>
                {cell}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const Ol = ({ items, color }: { items: string[]; color: string }) => (
  <List dense disablePadding sx={{ mb: 1 }}>
    {items.map((item, i) => (
      <ListItem
        key={i}
        disableGutters
        sx={{ alignItems: "flex-start", py: 0.25 }}
      >
        <Typography
          sx={{
            mr: 1,
            minWidth: 20,
            color,
            fontWeight: "bold",
            fontSize: "0.875rem",
          }}
        >
          {i + 1}.
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          {item}
        </Typography>
      </ListItem>
    ))}
  </List>
);

const QA = ({ q, a }: { q: string; a: string }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.25 }}>
      Q. {q}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
      A. {a}
    </Typography>
  </Box>
);

// インライン Markdown（**太字**）をレンダリング
function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// AI 回答テキストをブロック単位でレンダリング（リスト・太字・改行に対応）
function renderAiText(text: string): React.ReactNode {
  const lines = text.split("\n");
  const nodes: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const trimmed = raw.trim();

    if (trimmed === "") {
      if (nodes.length > 0) {
        nodes.push(<Box key={`gap-${i}`} sx={{ height: "0.5em" }} />);
      }
      continue;
    }

    // インデント量を px に変換（最大 24px）
    const indentLen = raw.match(/^(\s*)/)?.[1].length ?? 0;
    const pl = `${Math.min(Math.floor(indentLen / 2) * 12, 24)}px`;

    // 番号付きリスト: "1. " "1) " など
    const numMatch = trimmed.match(/^(\d+)[.)]\s+(.*)$/);
    if (numMatch) {
      nodes.push(
        <Box key={i} sx={{ display: "flex", gap: 0.5, mb: 0.25, pl }}>
          <Box
            component="span"
            sx={{
              minWidth: 22,
              flexShrink: 0,
              fontSize: "0.875rem",
              lineHeight: 1.7,
            }}
          >
            {numMatch[1]}.
          </Box>
          <Box component="span" sx={{ fontSize: "0.875rem", lineHeight: 1.7 }}>
            {renderInline(numMatch[2])}
          </Box>
        </Box>,
      );
      continue;
    }

    // 箇条書き: "* " "- " "• "
    const bulletMatch = trimmed.match(/^[*\-•]\s+(.*)$/);
    if (bulletMatch) {
      nodes.push(
        <Box
          key={i}
          sx={{ display: "flex", gap: 0.5, mb: 0.25, pl: `calc(${pl} + 4px)` }}
        >
          <Box
            component="span"
            sx={{ flexShrink: 0, fontSize: "0.875rem", lineHeight: 1.7 }}
          >
            •
          </Box>
          <Box component="span" sx={{ fontSize: "0.875rem", lineHeight: 1.7 }}>
            {renderInline(bulletMatch[1])}
          </Box>
        </Box>,
      );
      continue;
    }

    // 通常テキスト
    nodes.push(
      <Box
        key={i}
        component="span"
        sx={{
          display: "block",
          fontSize: "0.875rem",
          lineHeight: 1.7,
          mb: 0.1,
        }}
      >
        {renderInline(trimmed)}
      </Box>,
    );
  }

  return <>{nodes}</>;
}

// ===== AI チャットタブ =====

const THINKING_POEM = [
  "Here, the deep sorrow and sense of loss currently being experienced are expressed directly.",
  "Whether it's parting with a loved one or facing a major setback,",
  "it captures that rock-bottom feeling of thinking,",
  "\"It's so painful I can't even cry anymore\"",
  'or "I might never be able to smile again for the rest of my life."',
  "This part deeply empathizes with the listener's pain and suffering, offering comfort.",
  "",
  "To the self (or the listener) standing on the brink of despair,",
  'the song presents "the healing power of time."',
  "No matter how unbearable the sorrow may be,",
  "when viewed on the long scale of life,",
  'it will eventually be transformed into a "time (a past event)."',
  "It is a message of powerful hope and comfort,",
  "assuring us that the day will surely come when we overcome that sorrow",
  'and look back with a smile, saying, "We went through that, didn\'t we?"',
  "",
  "It gently reminds us that since a day when we can laugh is surely waiting in the future,",
  "there's no need to worry too much or let ourselves be bound by sadness in this very moment.",
  'The phrase "Let\'s be carried by today\'s wind" embodies a "flexible strength"—',
  "the idea of not forcing ourselves to resist inescapable fate or sorrow,",
  "but instead accepting the present (today) as it is and living naturally.",
  "",
  "Here, the perspective shifts abruptly from personal sorrow",
  "to the grand cycle of the world and life (reincarnation).",
  "Joy and sorrow alternate in life (the times turn),",
  "and no sorrow lasts forever.",
  "Even if two people face a definitive parting today,",
  "within the ever-turning cycle of time,",
  "they will one day meet again in a different form (or perhaps truly reborn).",
  'The song celebrates this vast concept of "fate" and "destiny."',
];

const AiChatTab = ({
  color,
  isDark,
  honbuId,
  roomId,
  onTaskMutated,
}: {
  color: string;
  isDark: boolean;
  honbuId?: number;
  roomId?: number;
  onTaskMutated?: () => void;
}) => {
  const theme = useTheme();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [poemLines, setPoemLines] = useState<string[]>([]);
  const poemIndexRef = useRef(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading) {
      setPoemLines([]);
      poemIndexRef.current = 0;
      return;
    }
    const interval = setInterval(() => {
      const idx = poemIndexRef.current;
      setPoemLines((prev) => [
        ...prev,
        THINKING_POEM[idx % THINKING_POEM.length],
      ]);
      poemIndexRef.current = idx + 1;
    }, 200);
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, poemLines]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");

    const next: ChatMessage[] = [...messages, { role: "user", text }];
    setMessages(next);
    setLoading(true);

    try {
      const res = await fetch("/api/helpbot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          honbuId: honbuId ?? 0,
          roomId: roomId ?? 0,
          messages: next,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      let displayText = data.text as string;
      if (data.executedAction) {
        const action = data.executedAction as { success: boolean; message?: string };
        const badge = action.success ? "✅" : "⚠️";
        displayText = `${displayText}\n\n${badge} ${action.message ?? ""}`;
        if (action.success) onTaskMutated?.();
      }
      setMessages([...next, { role: "model", text: displayText }]);
    } catch {
      setMessages([
        ...next,
        {
          role: "model",
          text: "エラーが発生しました。もう一度お試しください。",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const bgUser = alpha(color, 0.12);
  const bgModel = isDark ? "#2a2a2a" : "#f5f5f5";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* メッセージ一覧 */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5 }}>
        {messages.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 5,
              px: 2,
              gap: 1.5,
            }}
          >
            <SmartToyTwoToneIcon sx={{ fontSize: 56, color }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", lineHeight: 1.8 }}
            >
              ChatisのTODO機能について何でも聞いてください
            </Typography>
            <Box
              sx={{
                width: "100%",
                mt: 0.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ textAlign: "center" }}
              >
                ー質問例ー
              </Typography>
              {[
                "タスクの作り方を教えて",
                "今週中に自分がやるべきタスクは？",
                "過去の傾向から今月も作成すべきタスクは？",
                "〇〇さんの担当タスクを教えて",
              ].map((example) => (
                <Box
                  key={example}
                  onClick={() => setInput(example)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    border: `1px solid ${alpha(color, 0.35)}`,
                    bgcolor: alpha(color, 0.05),
                    cursor: "pointer",
                    "&:hover": { bgcolor: alpha(color, 0.12) },
                    transition: "background-color 0.15s",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {example}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                maxWidth: "85%",
                bgcolor: msg.role === "user" ? bgUser : bgModel,
                borderRadius:
                  msg.role === "user"
                    ? "12px 12px 2px 12px"
                    : "12px 12px 12px 2px",
                px: 1.5,
                py: 1,
              }}
            >
              {msg.role === "model" ? (
                <Box sx={{ wordBreak: "break-word" }}>
                  {renderAiText(msg.text)}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.7,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.text}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.5 }}>
            <Box
              sx={{
                bgcolor: bgModel,
                borderRadius: "12px 12px 12px 2px",
                px: 1.5,
                py: 1,
                maxWidth: "85%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: poemLines.length > 0 ? 1 : 0,
                }}
              >
                <CircularProgress size={14} sx={{ color }} />
                <Typography variant="body2" color="text.secondary">
                  考え中...
                </Typography>
              </Box>
              {poemLines.length > 0 && (
                <Box
                  sx={{ borderTop: `1px solid ${alpha(color, 0.2)}`, pt: 1 }}
                >
                  {poemLines.map((line, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.disabled",
                        fontStyle: "italic",
                        lineHeight: line === "" ? 0.8 : 1.6,
                        opacity: i === poemLines.length - 1 ? 1 : 0.6,
                        transition: "opacity 0.5s ease",
                        fontSize: "0.7rem",
                      }}
                    >
                      {line === "" ? " " : line}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      {/* 入力エリア */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${
            isDark ? "rgba(255,255,255,0.28)" : theme.palette.divider
          }`,
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="質問を入力..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: color },
            },
          }}
        />
        <IconButton
          onClick={send}
          disabled={!input.trim() || loading}
          sx={{
            color: !input.trim() || loading ? "text.disabled" : color,
            mb: 0.25,
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

// ===== メインコンポーネント =====

export const HelpDrawer = ({
  open,
  onClose,
  paletteColor,
  isDark = false,
  variant = "overlay",
  honbuId,
  roomId,
  isMobile = false,
  onTaskMutated,
}: Props) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [expanded, setExpanded] = useState<string[]>(() =>
    getDefaultExpanded(),
  );
  // 起動中のチュートリアルID（null = 非表示）
  const [tutorialId, setTutorialId] = useState<string | null>(null);

  const mainColor = PaletteColorHex[paletteColor];
  const headerBorder = isDark ? "#000000" : "#ffffff";
  // ダークモードでは背景を濃いめの PaletteColor にし、文字・アイコン・アクセントは白系にする
  const accent = isDark ? "#ffffff" : mainColor;
  const deepBg = darken(mainColor, 0.72);
  // Box 系の分割線（タブ下線・枠線）はクラス指定で拾えないため個別に明色化する
  const dividerCol = isDark ? "rgba(255,255,255,0.28)" : "divider";

  const handleAccordion = (id: string) => {
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const panelContent = (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        ...(isDark && {
          bgcolor: deepBg,
          // 基底文字色を白系にし、Typography 以外のインライン要素にも継承させる
          color: "rgba(255,255,255,0.92)",
          // 文字・アイコンを白系に統一（アコーディオンは濃色背景を透過させる）
          "& .MuiTypography-root": { color: "rgba(255,255,255,0.92)" },
          "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.9)" },
          // タブ・テーブルは Typography ではないため個別に白化する
          "& .MuiTab-root": { color: "rgba(255,255,255,0.7)" },
          "& .MuiTab-root.Mui-selected": { color: "#ffffff" },
          "& .MuiTableCell-root": {
            color: "rgba(255,255,255,0.92)",
            borderColor: "rgba(255,255,255,0.28)",
          },
          // 分割線を明るくして濃色背景でも見えるようにする
          "& .MuiAccordion-root": {
            backgroundColor: "transparent",
            borderColor: "rgba(255,255,255,0.28)",
          },
        }),
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          background: `linear-gradient(to right, ${mainColor}, ${alpha(mainColor, 0.7)})`,
          color: "#fff",
          borderBottom: `3px solid ${headerBorder}`,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          ヘルプ
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* タブ */}
      <Box sx={{ borderBottom: 1, borderColor: dividerCol, flexShrink: 0 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          slotProps={{
            indicator: { style: { backgroundColor: accent } },
          }}
          sx={{ minHeight: 35 }}
        >
          <Tab
            label="作成ガイド"
            sx={{ fontSize: "0.75rem", minHeight: 35, py: 0 }}
          />
          <Tab
            label="よくある質問"
            sx={{ fontSize: "0.75rem", minHeight: 35, py: 0 }}
          />
          <Tab
            label="AIに質問"
            sx={{ fontSize: "0.75rem", minHeight: 35, py: 0 }}
          />
          <Tab
            label="チュートリアル"
            sx={{ fontSize: "0.75rem", minHeight: 35, py: 0 }}
          />
        </Tabs>
      </Box>

      {/* コンテンツ */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* AIに質問タブ */}
        {tabIndex === 2 && (
          <AiChatTab
            color={accent}
            isDark={isDark}
            honbuId={honbuId}
            roomId={roomId}
            onTaskMutated={onTaskMutated}
          />
        )}

        {/* 作成ガイドタブ */}
        {tabIndex === 0 && (
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <>
              {/* クイックガイド */}
              <Accordion
                expanded={expanded.includes("guide-quick")}
                onChange={() => handleAccordion("guide-quick")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    クイックガイド
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  <CompactTable
                    color={accent}
                    headers={["やりたいこと", "操作場所"]}
                    rows={
                      isMobile
                        ? [
                            ["プロジェクト作成", "右上の＋ボタン（新規作成）"],
                            ["タスク追加", "下部中央の大きな丸い＋ボタン"],
                            ["タスク詳細・編集", "タスク行をタップ"],
                            [
                              "タスク完了",
                              "タスク左端のチェックボックスをタップ",
                            ],
                            ["フィルター設定", "下部の絞り込みアイコン"],
                            ["タグ管理", "右上の設定アイコン → タグ管理"],
                            ["繰り返し設定", "タスク詳細 → 繰り返し"],
                          ]
                        : [
                            ["プロジェクト作成", "左サイドバー＋ボタン"],
                            ["タスク追加", "右下＋ボタン or タスクを新規作成"],
                            ["タスク詳細・編集", "タスク行をクリック"],
                            ["タスク完了", "タスク左端のチェックボックス"],
                            ["フィルター設定", "左サイドバー「フィルター」"],
                            ["タグ管理", "設定 → タグ管理"],
                            ["繰り返し設定", "タスク詳細 → 繰り返し"],
                            ["ビュー切替", "ツールバーのアイコン"],
                          ]
                    }
                  />
                </AccordionDetails>
              </Accordion>

              {/* ① プロジェクトとタスクの作成 */}
              <Accordion
                expanded={expanded.includes("g1")}
                onChange={() => handleAccordion("g1")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    ① プロジェクトとタスクの作成
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  <H3 color={accent}>プロジェクトを作成する</H3>
                  <Ol
                    color={accent}
                    items={
                      isMobile
                        ? [
                            "画面右上の＋ボタン（新規作成）をタップ",
                            "プロジェクト名を入力して「作成」を押す",
                            "テンプレートがある場合はテンプレートから作成することも可能",
                          ]
                        : [
                            "左サイドバーの「プロジェクト」欄にある＋ボタンをクリック",
                            "プロジェクト名を入力して「作成」を押す",
                            "テンプレートがある場合はテンプレートから作成することも可能",
                          ]
                    }
                  />
                  <H3 color={accent}>タスクを追加する</H3>
                  <Ol
                    color={accent}
                    items={
                      isMobile
                        ? [
                            "プロジェクトを選択した状態で、下部中央の大きな丸い＋ボタンをタップ",
                            "タスク名を入力し、必要な情報（期限など）を設定",
                            "「保存」を押して作成完了",
                          ]
                        : [
                            "プロジェクトを選択した状態で、右下の＋ボタンをクリック",
                            "タスク名を入力し、必要な情報（期限・担当者など）を設定",
                            "「保存」を押して作成完了",
                            "リスト下部の「タスクを新規作成」リンクからも追加可能",
                          ]
                    }
                  />
                </AccordionDetails>
              </Accordion>

              {/* ② タスクの詳細設定 */}
              <Accordion
                expanded={expanded.includes("g2")}
                onChange={() => handleAccordion("g2")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    ② タスクの詳細設定
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  <H3 color={accent}>設定できる項目</H3>
                  <CompactTable
                    color={accent}
                    headers={["項目", "説明"]}
                    rows={[
                      [
                        "ステータス",
                        "未対応 / 対応中 / 完了 / 非表示（ルームごとにカスタマイズ可）",
                      ],
                      ["優先度", "高 / 中 / 低 / なし"],
                      ["開始日・終了日", "タスクの期間を設定"],
                      ["担当者", "ルームメンバーを選択（ルームモードのみ）"],
                      ["依頼者", "依頼した人を設定（ルームモードのみ）"],
                      ["タグ", "カラー付きのタグでカテゴリ管理"],
                      ["メモ", "タスクに関するメモを自由に記入"],
                      ["通知", "タスク完了時にChatisfへ通知を送信"],
                      ["スター", "重要タスクにスターを付けて強調"],
                    ]}
                  />
                  <H3 color={accent}>サブタスク（子タスク）</H3>
                  <P>
                    タスクの種類を「複数タスク」にすると、サブタスクを追加できます。サブタスクはそれぞれ個別に担当者・期限を設定可能です。
                  </P>
                  <Warn>
                    初期ステータスは「未対応 / 対応中 / 完了 /
                    非表示」の4種類です。全般設定からルームごとにカスタムステータスを追加できます。
                  </Warn>
                </AccordionDetails>
              </Accordion>

              {/* ③ フィルターと表示設定 */}
              <Accordion
                expanded={expanded.includes("g3")}
                onChange={() => handleAccordion("g3")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    ③ フィルターと表示設定
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  <H3 color={accent}>クイックフィルター</H3>
                  <P>
                    {isMobile
                      ? "下部ツールバーの絞り込みアイコンをタップして、よく使うフィルターをトグルできます。"
                      : "左サイドバーの「フィルター」セクションでよく使うフィルターをトグルできます。"}
                  </P>
                  <CompactTable
                    color={accent}
                    headers={["フィルター", "説明"]}
                    rows={[
                      [
                        "自分が担当者",
                        "自分がアサインされているタスクのみ表示",
                      ],
                      ["自分が依頼者", "自分が依頼したタスクのみ表示"],
                      [
                        "期限が今日のタスク",
                        "期限が今日以前のタスクを強調表示",
                      ],
                      ["スター付き", "スターを付けたタスクのみ表示"],
                      ["完了タスクを表示", "完了済みタスクも一覧に含める"],
                      ["非表示タスクを表示", "非表示状態のタスクも表示"],
                    ]}
                  />
                  <H3 color={accent}>詳細フィルター（Myフィルター）</H3>
                  <Ol
                    color={accent}
                    items={
                      isMobile
                        ? [
                            "下部の絞り込みアイコンをタップ",
                            "担当者・タグ・期間・優先度などで絞り込み条件を設定",
                            "「Myフィルターとして保存」で頻繁に使う条件を保存できる",
                          ]
                        : [
                            "ツールバーの「詳細フィルター」アイコンをクリック",
                            "担当者・タグ・期間・優先度などで絞り込み条件を設定",
                            "「Myフィルターとして保存」で頻繁に使う条件を保存できる",
                            "保存したMyフィルターはサイドバーから1クリックで適用可能",
                          ]
                    }
                  />
                </AccordionDetails>
              </Accordion>

              {/* ④ 繰り返し・テンプレート */}
              <Accordion
                expanded={expanded.includes("g4")}
                onChange={() => handleAccordion("g4")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    ④ 繰り返し・テンプレート
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  <H3 color={accent}>繰り返しタスク</H3>
                  <Ol
                    color={accent}
                    items={[
                      "タスク詳細パネルの「繰り返し」をクリック",
                      "繰り返しの種類（毎日・毎週・毎月・毎年）を選択",
                      "期間・期限の設定をしてルールを保存",
                      "設定したスケジュールで自動的にタスクが作成される",
                    ]}
                  />
                  <H3 color={accent}>タスクテンプレート</H3>
                  <P>
                    よく使うタスクの設定をテンプレートとして保存しておくと、次回から簡単に同じ設定のタスクを作成できます。
                  </P>
                  <H3 color={accent}>プロジェクトテンプレート</H3>
                  <P>
                    プロジェクトメニュー（「…」アイコン）からプロジェクト全体をテンプレートとして保存できます。新規プロジェクト作成時にテンプレートから初期タスクを自動生成します。
                  </P>
                </AccordionDetails>
              </Accordion>

              {/* ⑤ 便利機能 */}
              <Accordion
                expanded={expanded.includes("g5")}
                onChange={() => handleAccordion("g5")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    ⑤ 便利機能
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  {!isMobile && (
                    <>
                      <H3 color={accent}>ビュー切替</H3>
                      <P>
                        ツールバーのアイコンで5種類のビューに切り替えられます：リスト
                        / テーブル / タスクツリー / タイムライン / ボード
                      </P>
                    </>
                  )}
                  <H3 color={accent}>リマインダー</H3>
                  <P>
                    タスク詳細からリマインダーを設定すると、指定日時にChatisfへ通知が届きます。
                  </P>
                  <H3 color={accent}>プロジェクトピン留め</H3>
                  <P>
                    {isMobile
                      ? "よく使うプロジェクトをピン留めすると上部に固定表示されます。プロジェクト一覧のピンアイコンからピン留めできます。"
                      : "よく使うプロジェクトをピン留めするとサイドバーの上部に固定表示されます。プロジェクトメニュー（「…」）からピン留めできます。"}
                  </P>
                  <H3 color={accent}>プロジェクトメモ</H3>
                  <P>
                    プロジェクトに共有メモを残せます。未読のメモがある場合は「NEW」バッジで通知されます。
                  </P>
                  {!isMobile && (
                    <>
                      <H3 color={accent}>デスクトップショートカット</H3>
                      <P>
                        全般設定からデスクトップにショートカットを作成できます。次回からチャットを開かなくてもTODOに直接アクセスできます。
                      </P>
                      <Hint color={accent}>
                        キーボードの <strong>?</strong>{" "}
                        キーでいつでもこのヘルプを表示できます。
                      </Hint>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          </Box>
        )}

        {/* よくある質問タブ */}
        {tabIndex === 1 && (
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <>
              {/* タスク・プロジェクトの操作について */}
              <Accordion
                expanded={expanded.includes("faq-edit")}
                onChange={() => handleAccordion("faq-edit")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    タスク・プロジェクトの操作について
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  <QA
                    q="完了したタスクが見えなくなった"
                    a="左サイドバーの「完了タスクを表示」トグルをオンにすると完了タスクが表示されます。"
                  />
                  <QA
                    q="タスクの順序を変えたい"
                    a="現時点では手動での並び替えには対応していません。ソート設定（作成日・ステータス・期限・優先度）を使って表示順を変更できます。"
                  />
                  <QA
                    q="タスクを別のプロジェクトに移動できる？"
                    a="現時点ではプロジェクト間のタスク移動には対応していません。テンプレート機能を使用して複製を行ってください。"
                  />
                  <QA
                    q="プロジェクトを非表示にしたい"
                    a="プロジェクト名横の「…」メニューから「非表示」を選択するとサイドバーから非表示になります。プロジェクト編集画面から再表示できます。"
                  />
                </AccordionDetails>
              </Accordion>

              {/* フィルター・表示について */}
              <Accordion
                expanded={expanded.includes("faq-filter")}
                onChange={() => handleAccordion("faq-filter")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    フィルター・表示について
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  <QA
                    q="Myフィルターを保存したい"
                    a={
                      isMobile
                        ? "下部の絞り込みアイコンをタップしてフィルターダイアログを開き、「Myフィルターとして保存」ボタンで名前を付けて保存できます。"
                        : "詳細フィルターダイアログで条件を設定後、「Myフィルターとして保存」ボタンで名前を付けて保存できます。"
                    }
                  />
                  {!isMobile && (
                    <QA
                      q="タイムラインビューで特定メンバーだけ表示したい"
                      a="フィルターで担当者を絞り込むと、タイムラインビューに表示されるメンバーも絞り込まれます。"
                    />
                  )}
                  <QA
                    q="タグで絞り込みたい"
                    a={
                      isMobile
                        ? "下部の絞り込みアイコンをタップしてフィルターダイアログを開き、タグ欄で絞り込みできます。AND条件・OR条件も切り替えられます。"
                        : "詳細フィルターダイアログのタグ欄で絞り込みできます。AND条件・OR条件も切り替えられます。"
                    }
                  />
                </AccordionDetails>
              </Accordion>

              {/* 繰り返し・テンプレートについて */}
              <Accordion
                expanded={expanded.includes("faq-manage")}
                onChange={() => handleAccordion("faq-manage")}
                elevation={0}
                disableGutters
                sx={{
                  "&:before": { display: "none" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ px: 2, py: 0.5, minHeight: 0 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    繰り返し・テンプレートについて
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
                  <QA
                    q="繰り返しルールを一覧で確認したい"
                    a="画面左の「繰り返し作成一覧」から確認・編集できます。"
                  />
                  <QA
                    q="繰り返しタスクの内容を変更したい"
                    a="画面左の「繰り返し作成一覧」から内容を変更してください。また、繰り返し元のタスクや繰り返し設定によって作成されたタスクを編集した際に、「繰り返し設定も更新する」を選択すると今後作成されるタスクにも反映されます。"
                  />
                  <QA
                    q="繰り返しタスクを止めたい"
                    a="画面左の「繰り返し作成一覧」からルールを削除すると、以降のタスクは自動作成されなくなります。既に作成済みのタスクは残ります。"
                  />
                  <QA
                    q="テンプレートを他のプロジェクトでも使いたい"
                    a="テンプレート保存時に「共有」をオンにすると、同じルームの他のメンバーもそのテンプレートを使用できます。"
                  />
                </AccordionDetails>
              </Accordion>
            </>
          </Box>
        )}

        {/* チュートリアルタブ */}
        {tabIndex === 3 && (
          <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, lineHeight: 1.7 }}
            >
              実際の画面に似た練習用の画面で、操作を体験しながら使い方を学べます。
              ここでの操作は実際のデータには保存されません。
            </Typography>

            {/* 基本チュートリアル */}
            <Box
              onClick={() => setTutorialId("basic")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(accent, 0.35)}`,
                bgcolor: alpha(accent, 0.05),
                cursor: "pointer",
                "&:hover": { bgcolor: alpha(accent, 0.12) },
                transition: "background-color 0.15s",
              }}
            >
              <SchoolTwoToneIcon sx={{ color: accent, fontSize: 32 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  プロジェクトの作成とタスクの作成・編集
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.3 }}
                >
                  プロジェクト作成 → タスク作成 → 詳細編集 → 完了・確認
                </Typography>
              </Box>
              <PlayArrowRoundedIcon sx={{ color: accent }} />
            </Box>

            {/* ルームごとの設定チュートリアル */}
            <Box
              onClick={() => setTutorialId("settings")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(accent, 0.35)}`,
                bgcolor: alpha(accent, 0.05),
                cursor: "pointer",
                "&:hover": { bgcolor: alpha(accent, 0.12) },
                transition: "background-color 0.15s",
              }}
            >
              <SchoolTwoToneIcon sx={{ color: accent, fontSize: 32 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  ルームごとの設定（ステータス・タグ）
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.3 }}
                >
                  チャットルームごとに設定ができるステータス（完了・未完了など）やタグを管理できます。
                </Typography>
              </Box>
              <PlayArrowRoundedIcon sx={{ color: accent }} />
            </Box>

            {/* カスタム項目チュートリアル */}
            <Box
              onClick={() => setTutorialId("custom")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(accent, 0.35)}`,
                bgcolor: alpha(accent, 0.05),
                cursor: "pointer",
                "&:hover": { bgcolor: alpha(accent, 0.12) },
                transition: "background-color 0.15s",
              }}
            >
              <SchoolTwoToneIcon sx={{ color: accent, fontSize: 32 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  カスタム項目の設定と表示
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.3 }}
                >
                  プロジェクトごとに設定ができるカスタム項目を管理できます。
                </Typography>
              </Box>
              <PlayArrowRoundedIcon sx={{ color: accent }} />
            </Box>

            {/* ダッシュボードのカスタマイズチュートリアル（PC 版限定） */}
            {!isMobile && (
              <Box
                onClick={() => setTutorialId("dashboard")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha(accent, 0.35)}`,
                  bgcolor: alpha(accent, 0.05),
                  cursor: "pointer",
                  "&:hover": { bgcolor: alpha(accent, 0.12) },
                  transition: "background-color 0.15s",
                }}
              >
                <SchoolTwoToneIcon sx={{ color: accent, fontSize: 32 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    ダッシュボードのカスタマイズ
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.3 }}
                  >
                    スコアカードと列表示を、共通・個人それぞれで自由にカスタマイズできます（PC版限定）。
                  </Typography>
                </Box>
                <PlayArrowRoundedIcon sx={{ color: accent }} />
              </Box>
            )}

            {/* 繰り返し作成チュートリアル */}
            <Box
              onClick={() => setTutorialId("recurrence")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(accent, 0.35)}`,
                bgcolor: alpha(accent, 0.05),
                cursor: "pointer",
                "&:hover": { bgcolor: alpha(accent, 0.12) },
                transition: "background-color 0.15s",
              }}
            >
              <SchoolTwoToneIcon sx={{ color: accent, fontSize: 32 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  繰り返し作成の設定と編集
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.3 }}
                >
                  繰り返し作成設定を行うことで、毎月・毎週などの指定のタイミングでタスクが自動作成されるようになります。
                </Typography>
              </Box>
              <PlayArrowRoundedIcon sx={{ color: accent }} />
            </Box>

            {/* リマインダーチュートリアル */}
            <Box
              onClick={() => setTutorialId("reminder")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: 1.5,
                borderRadius: 2,
                border: `1px solid ${alpha(accent, 0.35)}`,
                bgcolor: alpha(accent, 0.05),
                cursor: "pointer",
                "&:hover": { bgcolor: alpha(accent, 0.12) },
                transition: "background-color 0.15s",
              }}
            >
              <SchoolTwoToneIcon sx={{ color: accent, fontSize: 32 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  リマインダーの作成方法と自動設定
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.3 }}
                >
                  タスクごとに手動でリマインダーを設定する方法と、プロジェクト単位で自動的にリマインダーを付ける設定を学べます。
                </Typography>
              </Box>
              <PlayArrowRoundedIcon sx={{ color: accent }} />
            </Box>

            {/* 今後追加予定 */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: dividerCol,
                textAlign: "center",
              }}
            >
              <Typography variant="caption" color="text.disabled">
                他のチュートリアルは順次追加予定です
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* チュートリアル本体（全画面・最前面） */}
      {(tutorialId === "basic" ||
        tutorialId === "settings" ||
        tutorialId === "custom" ||
        tutorialId === "recurrence" ||
        tutorialId === "reminder" ||
        tutorialId === "dashboard") && (
        <TutorialSandbox
          paletteColor={paletteColor}
          isDark={isDark}
          variant={
            tutorialId === "settings"
              ? "settings"
              : tutorialId === "custom"
                ? "custom"
                : tutorialId === "recurrence"
                  ? "recurrence"
                  : tutorialId === "reminder"
                    ? "reminder"
                    : tutorialId === "dashboard"
                      ? "dashboard"
                      : "basic"
          }
          onClose={() => setTutorialId(null)}
        />
      )}
    </Box>
  );

  if (variant === "panel") {
    return (
      <Box
        sx={{
          position: "fixed",
          right: 0,
          top: 0,
          height: "100vh",
          width: open ? DRAWER_WIDTH : 0,
          // MUIダイアログ(z:1300)・バックドロップ(z:1299)より上に表示し、暗転に巻き込まれないようにする
          zIndex: 1500,
          overflow: "hidden",
          bgcolor: "background.paper",
          transition: (theme) =>
            open
              ? theme.transitions.create("width", {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                })
              : theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
          borderLeft: open ? "1px solid" : "none",
          borderColor: "divider",
        }}
      >
        <Box sx={{ width: DRAWER_WIDTH, height: "100%", overflowY: "auto" }}>
          {panelContent}
        </Box>
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: DRAWER_WIDTH },
        },
      }}
    >
      {panelContent}
    </Drawer>
  );
};
